import { useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  Autocomplete,
  Box,
  createFilterOptions,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

import {
  buildPhoneValue,
  COUNTRY_OPTIONS,
  detectDefaultCountry,
  flagImageUrls,
  formatNationalNumber,
  getCountryOption,
  parsePhoneValue,
} from "./utils";

import type { CountryCode } from "libphonenumber-js";
import type { ChangeEvent } from "react";

import type { CountryOption } from "./utils";

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: VoidFunction;
  error?: boolean;
  helperText?: string;
  defaultCountry?: CountryCode;
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
}

const filterOptions = createFilterOptions<CountryOption>({
  stringify: (option) => `${option.name} ${option.code} +${option.callingCode}`,
});

export function PhoneNumberField(props: Props) {
  const {
    label,
    value,
    onChange,
    onBlur,
    error = false,
    helperText,
    defaultCountry,
    autoComplete,
    autoFocus = false,
    disabled = false,
    fullWidth = false,
  } = props;

  // Fall back to the user's detected country (timezone, then locale) when the
  // caller does not pin one, so the dial code defaults to where they likely are.
  const effectiveDefaultCountry = useMemo(
    () => defaultCountry ?? detectDefaultCountry(),
    [defaultCountry],
  );

  const [country, setCountry] = useState<CountryCode>(
    () => parsePhoneValue(value, effectiveDefaultCountry).country,
  );
  const [nationalNumber, setNationalNumber] = useState(
    () => parsePhoneValue(value, effectiveDefaultCountry).nationalNumber,
  );
  const [lastSyncedValue, setLastSyncedValue] = useState(value);

  const inputRef = useRef<HTMLInputElement>(null);
  // Number of digits that should sit to the left of the caret after the field
  // is reformatted, so inserting/removing group separators does not jump it.
  const pendingCaretDigitsRef = useRef<number | null>(null);

  // Reconcile the split fields when the parent replaces the value (e.g.
  // resetting the form to an empty string) without clobbering local edits.
  // Adjusting state during render is React's recommended alternative to
  // synchronizing derived state in an effect.
  if (value !== lastSyncedValue) {
    setLastSyncedValue(value);
    if (value !== buildPhoneValue(country, nationalNumber)) {
      const parsed = parsePhoneValue(value, effectiveDefaultCountry);
      setCountry(parsed.country);
      setNationalNumber(parsed.nationalNumber);
    }
  }

  const handleCountryChange = (option: CountryOption | null) => {
    const nextCountry = option?.code ?? effectiveDefaultCountry;
    setCountry(nextCountry);
    onChange(buildPhoneValue(nextCountry, nationalNumber));
  };

  const handleNationalNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const caret = event.target.selectionStart ?? rawValue.length;
    const digitsBeforeCaret = rawValue
      .slice(0, caret)
      .replace(/\D/g, "").length;
    const trimmed = rawValue.trim();
    // Recover gracefully if a user pastes a full international number into the
    // national field instead of using the country selector.
    if (trimmed.startsWith("+")) {
      const parsed = parsePhoneNumberFromString(trimmed);
      if (parsed) {
        const nextCountry = parsed.country ?? country;
        setCountry(nextCountry);
        setNationalNumber(parsed.nationalNumber);
        onChange(buildPhoneValue(nextCountry, parsed.nationalNumber));
        pendingCaretDigitsRef.current = parsed.nationalNumber.length;
        return;
      }
    }
    const digits = trimmed.replace(/\D/g, "");
    // Refine the flag to the country the number actually belongs to (e.g. a
    // Canadian area code under the shared +1 code), but never let typing change
    // the dial code itself — only adopt a detected country with the same code.
    const currentCallingCode = getCountryCallingCode(country);
    const detected = parsePhoneNumberFromString(
      `+${currentCallingCode}${digits}`,
    );
    const nextCountry =
      detected?.country &&
      getCountryCallingCode(detected.country) === currentCallingCode
        ? detected.country
        : country;
    setCountry(nextCountry);
    setNationalNumber(digits);
    onChange(buildPhoneValue(nextCountry, digits));
    pendingCaretDigitsRef.current = digitsBeforeCaret;
  };

  // Restore the caret after the field reformats: walk the formatted value until
  // we have passed the same number of digits the caret previously sat behind.
  useLayoutEffect(() => {
    const targetDigits = pendingCaretDigitsRef.current;
    pendingCaretDigitsRef.current = null;
    const element = inputRef.current;
    if (targetDigits === null || !element) {
      return;
    }
    const { value: formatted } = element;
    let position = 0;
    let digitsSeen = 0;
    while (position < formatted.length && digitsSeen < targetDigits) {
      if (/\d/.test(formatted.charAt(position))) {
        digitsSeen += 1;
      }
      position += 1;
    }
    element.setSelectionRange(position, position);
  });

  const selectedOption =
    getCountryOption(country) ??
    getCountryOption(effectiveDefaultCountry) ??
    COUNTRY_OPTIONS[0];

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ width: fullWidth ? "100%" : "auto" }}
    >
      <Tooltip title="Select the country for your phone number's dialing code">
        <Autocomplete
          options={COUNTRY_OPTIONS}
          value={selectedOption}
          onChange={(_event, option) => handleCountryChange(option)}
          filterOptions={filterOptions}
          disableClearable
          disabled={disabled}
          autoHighlight
          autoSelect
          selectOnFocus
          handleHomeEndKeys
          blurOnSelect
          getOptionLabel={(option) => `+${option.callingCode}`}
          isOptionEqualToValue={(option, selected) =>
            option.code === selected.code
          }
          sx={{ width: 130, flexShrink: 0 }}
          slotProps={{ popper: { style: { width: 320 } } }}
          renderOption={(optionProps, option) => (
            <Box
              component="li"
              {...optionProps}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Box
                component="img"
                alt=""
                loading="lazy"
                width={20}
                sx={{ flexShrink: 0 }}
                {...flagImageUrls(option.code)}
              />
              <Box component="span" sx={{ flexGrow: 1, whiteSpace: "nowrap" }}>
                {option.name}
              </Box>
              <Box component="span" sx={{ color: "text.secondary" }}>
                +{option.callingCode}
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Code"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="img"
                      alt=""
                      width={20}
                      {...flagImageUrls(selectedOption.code)}
                    />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Tooltip>
      <TextField
        label={label}
        value={formatNationalNumber(country, nationalNumber)}
        onChange={handleNationalNumberChange}
        onBlur={onBlur}
        error={error}
        helperText={helperText}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        fullWidth
        inputRef={inputRef}
        inputProps={{ inputMode: "tel" }}
      />
    </Stack>
  );
}
